import React from 'react';
import ServiceFactory from '../../service';
import Markdown from './markdown';

/*
 * Data insights (2026-09-12) — on-demand x.ai summaries of the tenant's
 * rolled-up stats tables, one per insight type. Nothing is generated
 * overnight any more. They ride the single "Run AI Analysis" entry point:
 * the page calls generateAll(range) alongside the agent run and the drawer
 * renders them as a "Data insights" section under the market narrative
 * (tenant-wide, so never mixed into the per-market text).
 *   GET  /v1/sales/insights                         → types
 *   GET  /v1/sales/insights/{type}/{range}          → stored summary
 *   POST /v1/sales/insights/{type}/{range}/generate → 24h cache; cost recorded per tenant
 */

function svc(path) {
  const Service = ServiceFactory.getType(path, 'product');
  return new Service();
}
function fmt(d) {
  try {
    return new Date(d).toLocaleString();
  } catch (e) {
    return '';
  }
}
export function insightLabel(key) {
  return String(key || '').replace(/-stats-analysis$/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
export function useDataInsights() {
  const [types, setTypes] = React.useState([]);
  const [rows, setRows] = React.useState({}); // statsType → {analysis, createdDate, fresh, note}
  const [errors, setErrors] = React.useState({});
  const [running, setRunning] = React.useState(false);
  const [range, setRange] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    svc('/sales/insights').getAsync().then(d => alive && setTypes((Array.isArray(d) ? d : []).map(t => t.statsType || t.StatsType).filter(Boolean))).catch(() => alive && setTypes([]));
    return () => {
      alive = false;
    };
  }, []);

  // Stored summaries only (no provider calls) — e.g. reopening the last analysis.
  const load = React.useCallback(async r => {
    setRange(r);
    const out = {};
    await Promise.all(types.map(async key => {
      try {
        out[key] = await svc(`/sales/insights/${key}/${r}`).getAsync();
      } catch (e) {/* leave empty */}
    }));
    setRows(out);
  }, [types]);

  // Generate every type for the range (cached <24h ones return instantly).
  const generateAll = React.useCallback(async (r, force) => {
    if (!types.length) return;
    setRange(r);
    setRunning(true);
    setErrors({});
    await Promise.all(types.map(async key => {
      try {
        const d = await svc(`/sales/insights/${key}/${r}/generate${force ? '?force=true' : ''}`).postAsync({});
        setRows(prev => ({
          ...prev,
          [key]: d
        }));
      } catch (e) {
        let msg = "Couldn't generate.";
        try {
          msg = JSON.parse(e.message).error || msg;
        } catch (x) {/* plain */}
        setErrors(prev => ({
          ...prev,
          [key]: msg
        }));
      }
    }));
    setRunning(false);
  }, [types]);
  const reset = React.useCallback(() => {
    setRows({});
    setErrors({});
  }, []);
  return {
    types,
    rows,
    errors,
    running,
    range,
    load,
    generateAll,
    reset
  };
}

// Rendered inside the AI drawer under the market narrative.
export function DataInsightsSection({
  insights,
  onRefresh
}) {
  const [open, setOpen] = React.useState(null);
  if (!insights || !insights.types.length) return null;
  const {
    types,
    rows,
    errors,
    running
  } = insights;
  const dates = types.map(k => rows[k] && rows[k].createdDate).filter(Boolean).map(d => new Date(d).getTime());
  const newest = dates.length ? new Date(Math.max(...dates)) : null;
  const any = types.some(k => rows[k] && rows[k].analysis);
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-drawer-insights",
    style: {
      marginTop: 22,
      borderTop: '1px solid var(--plk-border,#e6e9ef)',
      paddingTop: 14
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      marginBottom: 6
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      margin: 0,
      fontSize: 15
    }
  }, /*#__PURE__*/React.createElement("i", {
    className: "material-icons",
    style: {
      fontSize: 17,
      verticalAlign: 'text-bottom',
      color: '#ff6358',
      marginRight: 6
    }
  }, "insights"), "Data insights"), /*#__PURE__*/React.createElement("span", {
    className: "territory-hint",
    style: {
      margin: 0,
      fontSize: 12
    }
  }, running ? 'Generating…' : newest ? `Generated ${fmt(newest)}` : any ? '' : 'Not generated yet', ' · ', "tenant-wide, all markets, for the selected range"), !running && onRefresh && /*#__PURE__*/React.createElement("a", {
    style: {
      fontSize: 12,
      cursor: 'pointer',
      marginLeft: 'auto'
    },
    onClick: () => onRefresh(true)
  }, "Refresh data insights")), types.map(key => {
    const r = rows[key] || {};
    const has = !!r.analysis;
    const isOpen = open === key;
    return /*#__PURE__*/React.createElement("div", {
      key: key,
      style: {
        borderBottom: '1px solid var(--plk-border,#eef0f3)'
      }
    }, /*#__PURE__*/React.createElement("div", {
      onClick: () => has && setOpen(isOpen ? null : key),
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 2px',
        cursor: has ? 'pointer' : 'default'
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons",
      style: {
        fontSize: 18,
        color: has ? '#ff6358' : '#c4c9d1'
      }
    }, isOpen ? 'expand_more' : 'chevron_right'), /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: 13.5,
        flex: '1 1 auto'
      }
    }, insightLabel(key)), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0,
        fontSize: 11.5
      }
    }, errors[key] ? errors[key] : has ? fmt(r.createdDate) : running ? '…' : r.note || 'No data for this range')), isOpen && has && /*#__PURE__*/React.createElement("div", {
      style: {
        padding: '0 6px 10px 28px'
      }
    }, /*#__PURE__*/React.createElement(Markdown, {
      source: r.analysis,
      className: "mi-summary-content"
    })));
  }));
}